import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  UiNewVersionRequest,
  UiReport,
  UiReportItem,
  UiReportSummaries,
  UiReportValidationErrorsResponse,
  UiReportVersionDetails,
  UiReportVersionHeader,
} from "@/generated/ui-api";
import * as sdk from "@/generated/ui-api";
import i18n from "@/i18n";

export const reportKeys = {
  all: () => ["reports", i18n.language] as const,
  lists: () => [...reportKeys.all(), "list"] as const,
  list: (filters: string) => [...reportKeys.lists(), { filters }] as const,
  details: (reportId?: number) =>
    [...reportKeys.all(), "details", reportId] as const,
  summaries: (startDate: string, endDate: string) =>
    [...reportKeys.all(), "summaries", { startDate, endDate }] as const,
  items: (
    versionId: number,
    page: number,
    pageSize: number,
    filters?: {
      reportItemNumberFrom?: string;
      reportItemNumberTo?: string;
      combinedNomenclatureCode?: string;
      partnerIdentifier?: string;
      error?: string;
      warning?: string;
      nonValidPartnerIndicator?: string;
      hideDeleted?: string;
    }
  ) =>
    [
      ...reportKeys.all(),
      "items",
      { versionId, page, pageSize, ...filters },
    ] as const,
  item: (versionId: number, itemId?: number) =>
    [...reportKeys.all(), "item", { versionId, itemId }] as const,
  header: (versionId: number) =>
    [...reportKeys.all(), "header", { versionId }] as const,
  errors: (
    versionId: number,
    page: number,
    pageSize: number,
    filters?: {
      reportItemNumberFrom?: string;
      reportItemNumberTo?: string;
      code?: string;
      title?: string;
      impactError?: boolean;
      impactWarning?: boolean;
      scopeReport?: boolean;
      scopeItem?: boolean;
    }
  ) =>
    [
      ...reportKeys.all(),
      "errors",
      { versionId, page, pageSize, ...filters },
    ] as const,
} as const;

export function useReportById(id?: number, enabled: boolean = true) {
  return useQuery({
    queryKey: reportKeys.details(id),
    queryFn: async (): Promise<UiReport | undefined> => {
      if (!id) return undefined;
      const response = await sdk.getReportById({
        path: { reportId: id },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: enabled && !!id && id > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useReportVersionHeader(
  versionId: number,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: reportKeys.header(versionId),
    queryFn: async (): Promise<UiReportVersionHeader> => {
      const response = await sdk.getReportVersionHeader({
        path: { versionId },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: enabled && !!versionId && versionId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useReportSummaries(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: reportKeys.summaries(startDate, endDate),
    queryFn: async (): Promise<UiReportSummaries> => {
      const response = await sdk.getReportSummaries({
        query: { startDate, endDate },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: enabled && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
  });
}

export function useReportItems(
  versionId: number,
  page: number,
  pageSize: number,
  filters: {
    reportItemNumberFrom?: string;
    reportItemNumberTo?: string;
    combinedNomenclatureCode?: string;
    partnerIdentifier?: string;
    error?: string;
    warning?: string;
    nonValidPartnerIndicator?: string;
    hideDeleted?: string;
    enabled?: boolean;
  } = {}
) {
  const { enabled, ...apiFilters } = filters;

  const queryParams = {
    page,
    pageSize,
    reportItemNumberFrom: apiFilters.reportItemNumberFrom
      ? parseInt(apiFilters.reportItemNumberFrom)
      : undefined,
    reportItemNumberTo: apiFilters.reportItemNumberTo
      ? parseInt(apiFilters.reportItemNumberTo)
      : undefined,
    combinedNomenclatureCode: apiFilters.combinedNomenclatureCode,
    partnerIdentifier: apiFilters.partnerIdentifier,
    error:
      apiFilters.error === "true"
        ? true
        : apiFilters.error === "false"
          ? false
          : undefined,
    warning:
      apiFilters.warning === "true"
        ? true
        : apiFilters.warning === "false"
          ? false
          : undefined,
    nonValidPartnerIndicator:
      apiFilters.nonValidPartnerIndicator === "true"
        ? true
        : apiFilters.nonValidPartnerIndicator === "false"
          ? false
          : undefined,
    hideDeleted:
      apiFilters.hideDeleted === "true"
        ? true
        : apiFilters.hideDeleted === "false"
          ? false
          : undefined,
  };

  return useQuery({
    queryKey: reportKeys.items(versionId, page, pageSize, apiFilters),
    queryFn: async (): Promise<UiReportVersionDetails> => {
      const response = await sdk.getReportVersionById({
        path: { versionId },
        query: queryParams,
        throwOnError: true,
      });
      return response.data;
    },
    enabled: enabled ?? (!!versionId && versionId > 0),
    staleTime: 5 * 60 * 1000,
  });
}

export function useReportItem(
  versionId: number,
  itemId?: number,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: reportKeys.item(versionId, itemId),
    queryFn: async (): Promise<UiReportItem | undefined> => {
      if (!itemId) return undefined;
      const response = await sdk.getReportItemById({
        path: { versionId, itemId },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: enabled && !!versionId && !!itemId,
  });
}

export function useCreateReportItem(versionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UiReportItem) => {
      return sdk.createReportItem({
        path: { versionId },
        body: data,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useUpdateReportItem(versionId: number, itemId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UiReportItem) => {
      return sdk.updateReportItem({
        path: { versionId, itemId },
        body: data,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useBulkDeleteReportItems(versionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemIds: number[]) => {
      return sdk.bulkDeleteReportItems({
        path: { versionId },
        body: itemIds,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useCopyReportItem(versionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: number) => {
      return sdk.copyReportItem({
        path: { versionId, itemId },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useCreateReportVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: 0,
    mutationFn: (data: UiNewVersionRequest) => {
      return sdk.createReportVersion({
        body: data,
        throwOnError: true,
      });
    },
    onSuccess: response => {
      const reportId = response.data?.reportHeader?.reportId;
      if (reportId) {
        queryClient.invalidateQueries({
          queryKey: reportKeys.details(reportId),
        });
      }
      queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useUpdateReportVersionHeader(versionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UiReportVersionHeader) => {
      return sdk.updateReportVersionHeader({
        path: { versionId },
        body: data,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useUpdateReportVersionZeroTrade(versionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (zeroTradeIndicator: boolean) => {
      return sdk.updateReportVersionZeroTrade({
        path: { versionId },
        body: zeroTradeIndicator,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.header(versionId) });
      queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useReportErrors(
  versionId: number,
  page: number,
  pageSize: number,
  filters: {
    reportItemNumberFrom?: string;
    reportItemNumberTo?: string;
    code?: string;
    title?: string;
    impactError?: boolean;
    impactWarning?: boolean;
    scopeReport?: boolean;
    scopeItem?: boolean;
    enabled?: boolean;
  } = {}
) {
  const { enabled, ...apiFilters } = filters;
  const queryParams = {
    page,
    pageSize,
    reportItemNumberFrom: apiFilters.reportItemNumberFrom
      ? parseInt(apiFilters.reportItemNumberFrom)
      : undefined,
    reportItemNumberTo: apiFilters.reportItemNumberTo
      ? parseInt(apiFilters.reportItemNumberTo)
      : undefined,
    code: apiFilters.code,
    title: apiFilters.title,
    impactError: apiFilters.impactError,
    impactWarning: apiFilters.impactWarning,
    scopeReport: apiFilters.scopeReport,
    scopeItem: apiFilters.scopeItem,
  };

  return useQuery({
    queryKey: reportKeys.errors(versionId, page, pageSize, apiFilters),
    queryFn: async (): Promise<UiReportValidationErrorsResponse> => {
      const response = await sdk.getReportErrors({
        path: { versionId },
        query: queryParams,
        throwOnError: true,
      });
      return response.data;
    },
    enabled: enabled ?? (!!versionId && versionId > 0),
    staleTime: 5 * 60 * 1000,
  });
}
